/* eslint-disable react/prop-types */
import useTranslation from '@/hooks/i18n';
import { useNavigate } from 'react-router-dom';
import AnimatedFadeInComponent from '../AnimatedFadeInComponent';
import { Select, SelectContent, SelectItemTextOnly, SelectTrigger, SelectValue } from './select';

export interface Tab {
  name: string;
  href?: string;
  onClick?: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>, tab?: Tab) => void;
  current: boolean;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function capitalizeFirstLetter(string?: string) {
  if (!string) return '';
  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
}

export default function Tabs({
  tabs,
  setTabs,
  selectClassNames,
  children,
  tabsClassesNames,
  textClassNames,
  preserveCase,
}: {
  tabs: Tab[];
  setTabs?: React.Dispatch<Tab[]>;
  enableEmptyOnClick?: boolean;
  selectClassNames?: string;
  children?: React.ReactNode;
  tabsClassesNames?: string;
  textClassNames?: string;
  preserveCase?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function onClickTab(
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.ChangeEvent<HTMLSelectElement>,
    tab: Tab,
  ) {
    event?.preventDefault();

    if (tab.onClick) {
      tab.onClick(event as React.MouseEvent<HTMLAnchorElement, MouseEvent>, tab);
    }

    if (tab.href) {
      navigate(tab.href);
      return;
    }
    if (setTabs) {
      setTabs([
        ...tabs.map((t) => {
          if (t.name === tab?.name) {
            return { ...t, current: true };
          } else {
            return { ...t, current: false };
          }
        }),
      ]);
    }
  }

  const selectedTabName = tabs.find((tab) => tab.current)?.name ?? '';

  const handleSelectChange = (value: string) => {
    const tab = tabs.find((t) => t.name === value);
    if (tab) {
      onClickTab(undefined as unknown as React.MouseEvent<HTMLAnchorElement, MouseEvent>, tab);
    }
  };

  return (
    <div>
      <div className={`mb-[30px] mt-[30px] md:hidden ${selectClassNames ?? ''}`}>
        <Select value={selectedTabName || undefined} onValueChange={handleSelectChange}>
          <SelectTrigger className="w-full" aria-label="Select a tab">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {tabs.map((tab) => (
              <SelectItemTextOnly key={tab.name} value={tab.name}>
                {preserveCase ? tab.name : t(tab.name)}
              </SelectItemTextOnly>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full">
        <AnimatedFadeInComponent>
          <div className="hidden w-full border-b border-people-green-100 md:block">
            <nav
              className={classNames(
                'inline-flex w-full items-center justify-center gap-8 rounded-md font-haffer text-sh-xxs',
                tabsClassesNames || '',
              )}
              aria-label="Tabs">
              {tabs.map((tab) => (
                <a
                  key={tab?.name}
                  href={tab.href}
                  onClick={(e) => onClickTab(e, tab)}
                  className={classNames(
                    tab.current
                      ? 'border-dark-green text-dark-green hover:!text-dark-green'
                      : 'border-transparent text-people-gray-500 hover:border-dark-green/50 hover:!text-dark-green',
                    '-mb-px cursor-pointer whitespace-nowrap border-b-2 py-1 font-haffer text-sh-xxs',
                    textClassNames || '',
                  )}
                  aria-current={tab.current ? 'page' : undefined}>
                  {preserveCase ? tab.name : capitalizeFirstLetter(t(tab?.name?.toLowerCase()))}
                </a>
              ))}
            </nav>
          </div>
        </AnimatedFadeInComponent>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}
